import React, { useState, useEffect } from 'react';
import { Plus, Edit, CheckCircle, XCircle, MoreHorizontal } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import rakePointService from '@/services/rakePointService';
import masterDataService from '@/services/masterDataService';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import PaginationComponent from '@/components/common/PaginationComponent';
import { formatTableText } from '@/lib/tableUtils';
import AddRakePointDialog from '../addRakePoint/AddRakePointDialog';
import EditRakePointDialog from '../editRakePoint/EditRakePointDialog';

import { useAuth } from '@/contexts/AuthContext';

const RakePointsManagementPage = () => {
  const { toast } = useToast();
  const { userProfile, userRole } = useAuth();
  const role = (userProfile?.role || userRole || '').toLowerCase();
  const canAccess = role === 'ipl_super_admin' || role === 'ipl_admin';

  const [rakePoints, setRakePoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [districtLookup, setDistrictLookup] = useState({});
  const [loadingLookup, setLoadingLookup] = useState(true);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRakePoint, setSelectedRakePoint] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchStatesAndDistricts();
  }, []);

  useEffect(() => {
    fetchRakePoints();
  }, [currentPage]);

  const fetchStatesAndDistricts = async () => {
    try {
      setLoadingLookup(true);
      const stateData = await masterDataService.getStates(true);
      if (Array.isArray(stateData)) {
        const mappedStates = stateData.map(s => ({ label: s.state_name || s.name || s.id, value: s.id }));
        const lookup = {};
        for (const state of mappedStates) {
          try {
            const districtData = await masterDataService.getDistrictsByState(state.value, false);
            if (Array.isArray(districtData)) {
              districtData.forEach(d => {
                lookup[d.id] = {
                  districtName: d.district_name || d.name || d.id,
                  stateName: state.label,
                  stateId: state.value
                };
              });
            }
          } catch (err) {
            console.error(`Failed to load lookup districts for state ${state.label}:`, err);
          }
        }
        setDistrictLookup(lookup);
      }
    } catch (err) {
      console.error('Failed to fetch states:', err);
    } finally {
      setLoadingLookup(false);
    }
  };

  const fetchRakePoints = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const response = await rakePointService.getRakePoints(skip, pageSize, false);

      const list = Array.isArray(response) ? response : response?.data || [];
      const total = Array.isArray(response) ? response.length : response?.total || list.length;

      setRakePoints(list);
      setTotalItems(total);
    } catch (err) {
      console.error('Failed to load rake points:', err);
      toast({ title: 'Error', description: 'Failed to load rake points.', variant: 'destructive' });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleCreateRakePoint = async (data) => {
    try {
      await rakePointService.createRakePoint(data);
      toast({ title: 'Success', description: 'Rake point created successfully.' });
      setAddDialogOpen(false);
      fetchRakePoints();
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'Failed to create rake point.', variant: 'destructive' });
    }
  };

  const handleUpdateRakePoint = async (data) => {
    try {
      await rakePointService.updateRakePoint(selectedRakePoint.id, data);
      toast({ title: 'Success', description: 'Rake point updated successfully.' });
      setEditDialogOpen(false);
      setSelectedRakePoint(null);
      fetchRakePoints();
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'Failed to update rake point.', variant: 'destructive' });
    }
  };

  const handleToggleActivate = async (rakePoint) => {
    try {
      const action = rakePoint.is_active ? 'deactivate' : 'activate';
      if (rakePoint.is_active) {
        await rakePointService.deactivateRakePoint(rakePoint.id);
      } else {
        await rakePointService.activateRakePoint(rakePoint.id);
      }
      toast({ title: 'Success', description: `Rake point ${action}d successfully.` });
      fetchRakePoints();
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'Failed to update status.', variant: 'destructive' });
    }
  };

  const handleOpenEdit = (rakePoint) => {
    setSelectedRakePoint(rakePoint);
    setEditDialogOpen(true);
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            Rake Points
          </h1>
          <p className="text-xs text-muted-foreground">
            Manage rake points
          </p>
        </div>
        <CommonButton onClick={() => setAddDialogOpen(true)} variant="default" icon={Plus}>
          Add Rake Point
        </CommonButton>
      </div>

      <TableBlurLoader isLoading={loading} className="mt-4">
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform">
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {rakePoints.length > 0 ? (
              <div className="rounded-md border overflow-hidden w-full">
                <div className="max-h-[calc(100vh-280px)] overflow-auto w-full">
                  <Table>
                    <TableHeader className="sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="h-10 px-4 py-1">Name</TableHead>
                        <TableHead className="h-10 px-4 py-1">Code</TableHead>
                        <TableHead className="h-10 px-4 py-1">State</TableHead>
                        <TableHead className="h-10 px-4 py-1">District</TableHead>
                        <TableHead className="h-10 px-4 py-1">Status</TableHead>
                        <TableHead className="h-10 px-4 py-1 text-right min-w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rakePoints.map((rp) => (
                        <TableRow key={rp.id} className="hover:bg-transparent border-border">
                          <TableCell className="h-10 px-4 py-1 font-medium">{formatTableText(rp.name)}</TableCell>
                          <TableCell className="h-10 px-4 py-1">{formatTableText(rp.code)}</TableCell>
                          <TableCell className="h-10 px-4 py-1">
                            {rp.district_id ? (
                              districtLookup[rp.district_id]?.stateName ||
                              (loadingLookup ? (
                                <div className="h-4 bg-slate-200 dark:bg-slate-800 animate-pulse rounded-[4px] w-20"></div>
                              ) : (
                                formatTableText(rp.district?.state?.name) || rp.state_name || '-'
                              ))
                            ) : (
                              formatTableText(rp.district?.state?.name) || rp.state_name || '-'
                            )}
                          </TableCell>
                          <TableCell className="h-10 px-4 py-1">
                            {rp.district_id ? (
                              districtLookup[rp.district_id]?.districtName ||
                              (loadingLookup ? (
                                <div className="h-4 bg-slate-200 dark:bg-slate-800 animate-pulse rounded-[4px] w-28"></div>
                              ) : (
                                formatTableText(rp.district?.name) || rp.district_name || '-'
                              ))
                            ) : (
                              formatTableText(rp.district?.name) || rp.district_name || '-'
                            )}
                          </TableCell>
                          <TableCell className="h-10 px-4 py-1">
                            <Badge variant={rp.is_active ? 'active' : 'delayed'} className="inline-block text-center w-[80px]">
                              {rp.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="h-10 px-4 py-1 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleOpenEdit(rp)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Rake Point
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleActivate(rp)}>
                                  {rp.is_active ? (
                                    <>
                                      <XCircle className="mr-2 h-4 w-4 text-amber-600" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="mr-2 h-4 w-4 text-emerald-600" />
                                      Activate
                                    </>
                                  )}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                No rake points available
              </div>
            )}
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              className="border-t mt-4"
            />
          </div>
        </div>
      </TableBlurLoader>

      <AddRakePointDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onCreateRakePoint={handleCreateRakePoint}
      />

      <EditRakePointDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setSelectedRakePoint(null);
        }}
        rakePoint={selectedRakePoint}
        onUpdateRakePoint={handleUpdateRakePoint}
      />
    </div>
  );
};

export default RakePointsManagementPage;
